// SPDX-License-Identifier: MIT

// Geo-targeted banner gating. Reads the visitor's location from a first-party
// cookie set by the CDN edge (CloudFront/Cloudflare/Vercel/Netlify/etc.). The
// edge writes the cookie as an ISO code, `<country>` or `<country>-<region>`
// (e.g. "FR", "CA-QC", "US-CA"); this module only reads it and decides whether
// the banner is required.
//
// With no scope configured, everyone is in scope (banner shown to all). When the
// scope is set but no edge cookie is present yet (edge not configured, or local
// dev), it fails OPEN (banner shown) unless geo.failOpen is false.

// EU 27 + EEA (Iceland, Liechtenstein, Norway) + UK + Switzerland. Included by
// default (geo.gdpr is true unless set to false); maintained here so the list
// updates for every site if the regime's membership changes.
const GDPR_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES',
  'SE', 'IS', 'LI', 'NO', 'GB', 'CH',
];

// California (CCPA/CPRA). Added via geo.ccpa: true. Note CCPA is an opt-out
// regime, so prompting here is stricter than the law requires.
const CCPA_REGIONS = ['US-CA'];

const DEFAULT_COOKIE = 'cc_geo';

// Reads the edge geo cookie into { country, region, continent } (any may be '').
const readGeoCookie = (name) => {
  if (typeof document === 'undefined') return {};
  try {
    const prefix = `${name}=`;
    const entry = document.cookie.split('; ').find((c) => c.indexOf(prefix) === 0);
    if (!entry) return {};
    const raw = decodeURIComponent(entry.slice(prefix.length)).trim();
    if (!raw) return {};
    const [country = '', region = '', continent = ''] = raw.split('-');
    return { country, region, continent };
  } catch (e) {
    return {};
  }
};

// Matches, most specific first: a region ("CA-QC"), a country ("FR"), or a
// continent ("EU"). Any list may be empty.
const isInScope = (loc, countries = [], regions = [], continents = []) => {
  const cc = String(loc.country || '').toUpperCase();
  const rc = String(loc.region || '').toUpperCase();
  const kk = String(loc.continent || '').toUpperCase();
  if (rc && regions.some((r) => String(r).toUpperCase() === `${cc}-${rc}`)) return true;
  if (countries.some((c) => String(c).toUpperCase() === cc)) return true;
  return continents.some((k) => String(k).toUpperCase() === kk);
};

// Builds the scope from config. gdpr (default true) adds the GDPR countries, ccpa
// adds California, and the banner* lists add to those.
const scopeFor = (geo) => ({
  countries: [...(geo.bannerCountries || []), ...(geo.gdpr !== false ? GDPR_COUNTRIES : [])],
  regions: [...(geo.bannerRegions || []), ...(geo.ccpa ? CCPA_REGIONS : [])],
  continents: geo.bannerContinents || [],
});

const hasScope = (s) => s.countries.length > 0 || s.regions.length > 0 || s.continents.length > 0;

// Returns true when the visitor must see the banner, false for implied consent.
// Synchronous: reads the edge cookie only. No scope → everyone; scope but no edge
// signal → geo.failOpen (default true).
const resolveGeoDecision = (geo = {}) => {
  const scope = scopeFor(geo);
  if (!hasScope(scope)) return true;

  const loc = readGeoCookie(geo.cookie || DEFAULT_COOKIE);
  if (!loc.country && !loc.region && !loc.continent) return geo.failOpen !== false;

  return isInScope(loc, scope.countries, scope.regions, scope.continents);
};

module.exports = {
  GDPR_COUNTRIES,
  CCPA_REGIONS,
  DEFAULT_COOKIE,
  isInScope,
  resolveGeoDecision,
};
